import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import berichtService from '../services/berichtService';
import protokollService from '../services/protokollService';
import erinnerungService from '../services/erinnerungService';
import { faelligkeit, erinnerungText } from '../utils/erinnerung';
import aufgabeService from '../services/aufgabeService';
import zeiterfassungService from '../services/zeiterfassungService';
import abwesenheitService from '../services/abwesenheitService';
import feiertagService from '../services/feiertagService';
import { Erinnerung, AufgabeStatus, User, ArbeitszeitAuswertung, UrlaubSaldo, FeiertagStatus } from '../types';
import { toast } from 'react-hot-toast';
import { Icon, PageHeader, Spinner, EmptyState, Hochzaehlen, fehlerText } from '../components/ui';
import GlobaleSuche from '../components/GlobaleSuche';

const iso = (d: Date) => d.toLocaleDateString('sv-SE');
const montagDieserWoche = () => {
  const x = new Date();
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
};
const fmtMin = (min: number) => `${Math.floor(Math.abs(min) / 60)}h ${(Math.abs(min) % 60).toString().padStart(2, '0')}m`;
const fmtSaldo = (min: number) => (min > 0 ? `+${fmtMin(min)}` : min < 0 ? `−${fmtMin(min)}` : '0h 00m');

const ERINNERUNGEN_PRO_SEITE = 5;

/** Schmaler Füllstand unter einer Kennzahl; läuft beim Erscheinen einmal auf seinen Wert. */
const Fuellstand: React.FC<{ anteil: number; farbe: string }> = ({ anteil, farbe }) => (
  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100" aria-hidden>
    <div className={`ui-fuellen h-full rounded-full ${farbe}`}
      style={{ transform: `scaleX(${Math.max(0, Math.min(1, anteil))})` }} />
  </div>
);

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const Dashboard: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ berichte: 0, protokolle: 0 });
  const [meineAufgaben, setMeineAufgaben] = useState(0);
  const [erinnerungen, setErinnerungen] = useState<Erinnerung[]>([]);
  const [erinnerungenLoading, setErinnerungenLoading] = useState(true);
  const [erinnerungSeite, setErinnerungSeite] = useState(0);
  const [blaetterRichtung, setBlaetterRichtung] = useState<'vor' | 'zurueck' | null>(null);
  const [arbeitszeit, setArbeitszeit] = useState<ArbeitszeitAuswertung | null>(null);
  const [saldo, setSaldo] = useState<UrlaubSaldo | null>(null);
  const [feiertagWarnung, setFeiertagWarnung] = useState<FeiertagStatus | null>(null);
  const [showAufgabeModal, setShowAufgabeModal] = useState(false);
  const [aufgabeThema, setAufgabeThema] = useState('');
  const [aufgabeStatuses, setAufgabeStatuses] = useState<AufgabeStatus[]>([]);
  const [aufgabeBenutzer, setAufgabeBenutzer] = useState<User[]>([]);
  const [aufgabeZugewiesenAn, setAufgabeZugewiesenAn] = useState<number | ''>('');
  const [aufgabeSaving, setAufgabeSaving] = useState(false);

  const loadErinnerungen = useCallback(async () => {
    try {
      const data = await erinnerungService.getPending();
      setErinnerungen(data);
    } catch {
      // silently fail — no toast on dashboard load
    } finally {
      setErinnerungenLoading(false);
    }
  }, []);

  useEffect(() => {
    Promise.all([
      berichtService.getOffeneAnzahl().catch(() => 0),
      protokollService.getUngeleseneAnzahl().catch(() => 0),
    ]).then(([berichte, protokolle]) => setStats({ berichte, protokolle }));

    aufgabeService.getAll().then(aufgaben => {
      const offen = aufgaben.filter(a => !a.istAbschluss && (a.zugewiesenAnId === user?.id || a.erstelltVonId === user?.id));
      setMeineAufgaben(offen.length);
    }).catch(() => {});

    aufgabeService.getAllStatus().then(s => setAufgabeStatuses(s)).catch(() => {});
    aufgabeService.getBenutzer().then(b => setAufgabeBenutzer(b)).catch(() => {});

    const mo = montagDieserWoche();
    const so = new Date(mo); so.setDate(so.getDate() + 6);
    zeiterfassungService.meine(iso(mo), iso(so)).then(setArbeitszeit).catch(() => {});
    abwesenheitService.meinSaldo().then(setSaldo).catch(() => {});

    if (isAdmin()) {
      const naechstesJahr = new Date().getFullYear() + 1;
      feiertagService.status(naechstesJahr)
        .then(s => { if (!s.vollstaendig) setFeiertagWarnung(s); })
        .catch(() => {});
    }

    loadErinnerungen();
  }, [loadErinnerungen, isAdmin, user?.id]);

  const handleMarkErledigt = async (id: number) => {
    try {
      await erinnerungService.markAsErledigt(id);
      setErinnerungen(prev => prev.filter(e => e.id !== id));
      toast.success('Erinnerung erledigt');
    } catch (err) {
      toast.error(fehlerText(err, 'Erinnerung konnte nicht als erledigt markiert werden'));
    }
  };

  const handleCreateAufgabe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aufgabeThema.trim()) return;
    const ersterStatus = aufgabeStatuses.find(s => !s.istAbschluss);
    if (!ersterStatus) return;
    setAufgabeSaving(true);
    try {
      await aufgabeService.create({
        thema: aufgabeThema.trim(),
        statusId: ersterStatus.id,
        zugewiesenAnId: aufgabeZugewiesenAn !== '' ? aufgabeZugewiesenAn : undefined,
      });
      setMeineAufgaben(prev => prev + 1);
      setShowAufgabeModal(false);
      setAufgabeThema('');
      setAufgabeZugewiesenAn('');
      toast.success('Aufgabe angelegt');
    } catch (err) {
      toast.error(fehlerText(err, 'Aufgabe konnte nicht angelegt werden'));
    } finally {
      setAufgabeSaving(false);
    }
  };

  const datierteErinnerungen = erinnerungen.filter(e => e.faelligAm);
  const erinnerungSeiten = Math.max(1, Math.ceil(datierteErinnerungen.length / ERINNERUNGEN_PRO_SEITE));
  const aktuelleErinnerungSeite = Math.min(erinnerungSeite, erinnerungSeiten - 1);
  const sichtbareErinnerungen = datierteErinnerungen.slice(
    aktuelleErinnerungSeite * ERINNERUNGEN_PRO_SEITE,
    (aktuelleErinnerungSeite + 1) * ERINNERUNGEN_PRO_SEITE,
  );

  return (
    <div className="p-4 md:p-6">
      <PageHeader title="Dashboard" subtitle={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`}
        actions={<div className="w-full md:w-[28rem]"><GlobaleSuche /></div>}
        className="[&>div:last-child]:w-full md:[&>div:last-child]:w-auto" />

      {/* Admin-Hinweis: Feiertage Folgejahr fehlen */}
      {feiertagWarnung && (
        <div className="mb-6 text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2"><Icon.Warnung className="h-4 w-4 flex-shrink-0" />Für {feiertagWarnung.jahr} sind noch keine Feiertage hinterlegt. Bitte in der Personal-Konfiguration abrufen.</span>
          <button onClick={() => navigate('/personal-konfiguration')} className="text-amber-900 underline whitespace-nowrap">Jetzt prüfen</button>
        </div>
      )}

      {/* Meine Zeit */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-6">
        {(() => {
          const heuteTag = arbeitszeit?.tage.find(t => t.datum === iso(new Date()));
          const eingeloggt = !!heuteTag?.kommen && heuteTag?.offen;
          return (
            <div
              className="bg-white rounded-lg shadow p-3 md:p-6 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate('/arbeitszeiten')}
            >
              <p className="text-xs md:text-sm text-gray-500">Heute</p>
              {eingeloggt ? (
                <p className="text-base md:text-lg font-bold text-green-600 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-green-500" aria-hidden />seit {heuteTag?.kommen}</p>
              ) : (
                <p className="text-base md:text-lg font-bold text-gray-400">nicht eingestempelt</p>
              )}
              {heuteTag && heuteTag.nettoMinuten > 0 && <p className="text-xs text-gray-500 mt-1">{fmtMin(heuteTag.nettoMinuten)} gearbeitet</p>}
            </div>
          );
        })()}
        <div className="bg-white rounded-lg shadow p-3 md:p-6 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/arbeitszeiten')}>
          <p className="text-xs md:text-sm text-gray-500">Woche Ist/Soll</p>
          <p className="text-base md:text-lg font-bold text-gray-900">{arbeitszeit ? `${fmtMin(arbeitszeit.summeNettoMinuten)}` : ''}</p>
          {arbeitszeit && <p className={`text-xs font-medium ${arbeitszeit.saldoMinuten >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtSaldo(arbeitszeit.saldoMinuten)}</p>}
          {arbeitszeit && arbeitszeit.summeNettoMinuten - arbeitszeit.saldoMinuten > 0 && (
            <Fuellstand
              anteil={arbeitszeit.summeNettoMinuten / (arbeitszeit.summeNettoMinuten - arbeitszeit.saldoMinuten)}
              farbe={arbeitszeit.saldoMinuten >= 0 ? 'bg-green-500' : 'bg-brand-500'} />
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-3 md:p-6 cursor-pointer hover:shadow-md transition-shadow col-span-2 md:col-span-1" onClick={() => navigate('/urlaub')}>
          <p className="text-xs md:text-sm text-gray-500">Resturlaub</p>
          <p className="text-base md:text-lg font-bold text-brand-700">{saldo ? `${saldo.restTage} Tage` : ''}</p>
          {saldo && saldo.restTage > 0 && new Date().getMonth() >= 9 && (
            <p className="text-xs text-amber-600 mt-1">bis 31.12. einplanen</p>
          )}
          {saldo && saldo.anspruchTage + saldo.uebertragTage > 0 && (
            <Fuellstand anteil={saldo.restTage / (saldo.anspruchTage + saldo.uebertragTage)} farbe="bg-brand-500" />
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-6 md:mb-8">
        {/* Aufgaben-Kachel – prominent */}
        <div
          className="col-span-2 md:col-span-1 rounded-lg shadow p-3 md:p-6 cursor-pointer transition-all hover:shadow-lg active:scale-95 bg-brand-600 text-white flex flex-col justify-between"
          onClick={() => navigate('/aufgaben')}
        >
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs md:text-sm font-medium text-brand-100">Meine Aufgaben</p>
            <Icon.Aufgaben className="w-4 h-4 text-brand-200" />
          </div>
          <p className={`text-2xl md:text-3xl font-bold ${meineAufgaben > 0 ? 'text-white' : 'text-brand-200'}`}>
            <Hochzaehlen wert={meineAufgaben} />
          </p>
          {meineAufgaben > 0 && (
            <p className="text-xs text-brand-200 mt-1">offen</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-3 md:p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/berichte')}>
          <p className="text-xs md:text-sm text-gray-500">Berichte offen</p>
          <p className={`text-2xl md:text-3xl font-bold ${stats.berichte > 0 ? 'text-amber-600' : 'text-gray-900'}`}><Hochzaehlen wert={stats.berichte} /></p>
        </div>

        <div className="bg-white rounded-lg shadow p-3 md:p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/protokolle')}>
          <p className="text-xs md:text-sm text-gray-500">Protokolle</p>
          <p className={`text-2xl md:text-3xl font-bold ${stats.protokolle > 0 ? 'text-blue-600' : 'text-gray-900'}`}><Hochzaehlen wert={stats.protokolle} /></p>
        </div>

        <div className="bg-white rounded-lg shadow p-3 md:p-6 col-span-2 md:col-span-1">
          <p className="text-xs md:text-sm text-gray-500">Erinnerungen</p>
          <p className={`text-2xl md:text-3xl font-bold ${erinnerungen.length > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
            {erinnerungenLoading ? '' : <Hochzaehlen wert={erinnerungen.length} />}
          </p>
        </div>
      </div>

      {/* Erinnerungen */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Icon.Glocke className="w-5 h-5 text-amber-500" />
          Anstehende Erinnerungen
        </h3>

        {erinnerungenLoading ? (
          <div className="flex items-center justify-center py-6 text-brand-600">
            <Spinner className="h-8 w-8" />
          </div>
        ) : datierteErinnerungen.length === 0 ? (
          <EmptyState title="Keine offenen Erinnerungen" className="!py-6" />
        ) : (
          <div key={aktuelleErinnerungSeite}
            className={`space-y-2 ${blaetterRichtung === 'vor' ? 'ui-blaettern-vor' : blaetterRichtung === 'zurueck' ? 'ui-blaettern-zurueck' : ''}`}>
            {sichtbareErinnerungen.map(e => {
              if (!e.faelligAm) return null;
              const status = faelligkeit(e.faelligAm);
              return (
                <div key={e.id} className={`rounded-lg p-3 flex items-center gap-3 ${status.card}`}>
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => navigate(`/kunden/${e.kundeId}`)}
                      className="text-sm font-medium text-gray-900 hover:text-brand-600 hover:underline text-left truncate block"
                    >
                      {e.kundeVorname ? `${e.kundeVorname} ${e.kundeNachnameFirma}` : e.kundeNachnameFirma}
                    </button>
                    <p className="text-xs text-gray-600 truncate">{erinnerungText(e)}</p>
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-end gap-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.badge}`}>
                      {status.label}
                    </span>
                    <span className="text-xs text-gray-400">{formatDate(e.faelligAm)}</span>
                  </div>
                  <button
                    onClick={() => handleMarkErledigt(e.id)}
                    title="Als erledigt markieren"
                    className="flex-shrink-0 p-1.5 rounded-full text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                  >
                    <Icon.Check className="w-5 h-5" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {erinnerungSeiten > 1 && (
          <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
            <span>
              {aktuelleErinnerungSeite * ERINNERUNGEN_PRO_SEITE + 1}–
              {Math.min((aktuelleErinnerungSeite + 1) * ERINNERUNGEN_PRO_SEITE, datierteErinnerungen.length)} von {datierteErinnerungen.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setBlaetterRichtung('zurueck'); setErinnerungSeite(aktuelleErinnerungSeite - 1); }}
                disabled={aktuelleErinnerungSeite === 0}
                title="Vorherige Seite"
                className="p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <Icon.ChevronLeft className="w-5 h-5" />
              </button>
              <span className="px-1 tabular-nums">{aktuelleErinnerungSeite + 1} / {erinnerungSeiten}</span>
              <button
                onClick={() => { setBlaetterRichtung('vor'); setErinnerungSeite(aktuelleErinnerungSeite + 1); }}
                disabled={aktuelleErinnerungSeite >= erinnerungSeiten - 1}
                title="Nächste Seite"
                className="p-1.5 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <Icon.ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3">Schnellaktionen</h3>
        <div className="space-y-2">
          <button onClick={() => navigate('/protokolle/neu')}
            className="w-full text-left px-4 py-3 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors text-sm md:text-base flex items-center gap-3">
            <Icon.Plus className="w-5 h-5 flex-shrink-0" />
            Inbetriebnahmeprotokoll anlegen
          </button>
          <button onClick={() => navigate('/kunden')}
            className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors text-sm md:text-base flex items-center gap-3">
            <Icon.Kunden className="w-5 h-5 flex-shrink-0 text-gray-400" />
            Kundenliste anzeigen
          </button>
          <button onClick={() => navigate('/waren')}
            className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors text-sm md:text-base flex items-center gap-3">
            <Icon.Paket className="w-5 h-5 flex-shrink-0 text-gray-400" />
            Artikel verwalten
          </button>
        </div>
      </div>
      {/* FAB – Neuer Bericht */}
      <button
        onClick={() => navigate('/berichte/neu')}
        title="Arbeitsbericht anlegen" aria-label="Arbeitsbericht anlegen"
        className="fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-white text-brand-600 border-2 border-brand-600 shadow-lg hover:bg-brand-50 active:scale-95 transition-all flex items-center justify-center"
      >
        <Icon.Bericht className="w-6 h-6" />
      </button>

      {/* FAB – Neue Aufgabe */}
      <button
        onClick={() => setShowAufgabeModal(true)}
        title="Aufgabe anlegen" aria-label="Aufgabe anlegen"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-brand-600 text-white shadow-lg hover:bg-brand-700 active:scale-95 transition-all flex items-center justify-center"
      >
        <Icon.Aufgaben className="w-6 h-6" />
      </button>

      {/* Aufgabe Quick-Create Modal */}
      {showAufgabeModal && (
        <div className="ui-fade fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={e => { if (e.target === e.currentTarget) setShowAufgabeModal(false); }}>
          <div className="ui-pop bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Icon.Aufgaben className="w-5 h-5 text-brand-600" />
                Aufgabe anlegen
              </h2>
              <button onClick={() => setShowAufgabeModal(false)} aria-label="Schließen"
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                <Icon.Close className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateAufgabe} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Thema</label>
                <input
                  autoFocus
                  type="text"
                  value={aufgabeThema}
                  onChange={e => setAufgabeThema(e.target.value)}
                  placeholder="Worum geht es?"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zuweisen an</label>
                <select
                  value={aufgabeZugewiesenAn}
                  onChange={e => setAufgabeZugewiesenAn(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
                >
                  <option value="">Niemandem zuweisen</option>
                  {aufgabeBenutzer.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.firstName} {b.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowAufgabeModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
                  Abbrechen
                </button>
                <button type="submit" disabled={!aufgabeThema.trim() || aufgabeSaving}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {aufgabeSaving ? 'Wird angelegt…' : 'Aufgabe anlegen'}
                </button>
              </div>
              <button type="button" onClick={() => { setShowAufgabeModal(false); navigate('/aufgaben'); }}
                className="w-full text-xs text-brand-600 hover:underline text-center pt-1">
                Alle Optionen in der Aufgaben-Ansicht
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
